import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import PageNavigation from "./PageNavigation";
import PageContent from "./PageContent";
import PageActions from "./PageActions";
import ConfirmationDialog from "../common/ConfirmationDialog";
import { checklistService } from "../../services/checklistService";
import {
  organizeAssetsIntoPages,
  calculatePageProgress,
  validatePageCompletion,
  canNavigateToPage,
  updatePageItem,
  findItemInPages,
} from "../../utils/pageUtils";

/**
 * ChecklistStepper - Main page-based container that manages page state and navigation
 * @param {Object} props
 * @param {Object} props.checklist - Checklist data with fsmAssets
 * @param {Function} props.onUpdateItem - Callback when an item is updated
 * @param {Function} props.onSave - Callback when save is triggered
 * @param {Function} props.onEdit - Callback when edit is triggered
 * @param {Function} props.onDelete - Callback when delete is triggered
 * @param {Function} props.onDuplicate - Callback when duplicate is triggered
 * @param {Object} props.fsmParams - URL parameters sent by FSM
 * @param {Function} props.onSetPdfDialogOpen - Callback when pdf has to be saved
 * @param {boolean} props.allowSkipping - Whether skipping pages is allowed
 * @param {boolean} props.requirePageCompletion - Whether page completion is required for navigation
 */
const ChecklistStepper = ({
  checklist,
  onUpdateItem,
  onSave,
  onEdit,
  onDelete,
  onDuplicate,
  // fsmParams = {},
  onSetPdfDialogOpen,
  allowSkipping = true,
  requirePageCompletion = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State management
  const [activePage, setActivePage] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Memoized pages organization
  const pages = useMemo(() => {
    if (!checklist?.fsmAssets) return [];
    return organizeAssetsIntoPages(checklist.fsmAssets);
  }, [checklist?.fsmAssets]);

  // Memoized progress calculation
  const progress = useMemo(() => {
    return calculatePageProgress(pages);
  }, [pages]);

  // Reset active page when checklist changes
  useEffect(() => {
    setActivePage(0);
    setHasUnsavedChanges(false);
  }, [checklist?.id]);

  // Auto-advance to next incomplete page on mount
  // useEffect(() => {
  //   if (pages.length > 0) {
  //     const nextIncompletePage = pages.findIndex((page) => !page.completed);
  //     if (nextIncompletePage !== -1 && nextIncompletePage !== activePage) {
  //       setActivePage(nextIncompletePage);
  //     }
  //   }
  // }, [pages, activePage]);

  if (!checklist) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No checklist data available
        </Typography>
      </Paper>
    );
  }

  if (pages.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          {checklist.templateName}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This checklist has no assets yet.
        </Typography>
      </Paper>
    );
  }

  const currentPage = pages[activePage];
  const isLastPage = activePage === pages.length - 1;
  const canGoNext =
    allowSkipping ||
    !requirePageCompletion ||
    currentPage?.completed ||
    isLastPage;
  const canGoPrevious = activePage > 0;

  const handlePageClick = (pageIndex) => {
    if (canNavigateToPage(pages, pageIndex, activePage, allowSkipping)) {
      setActivePage(pageIndex);
    } else {
      setSnackbar({
        open: true,
        message: "Please complete the current page before proceeding.",
        severity: "warning",
      });
    }
  };

  const handlePrevious = () => {
    if (canGoPrevious) {
      setActivePage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext) {
      if (isLastPage) {
        // Handle checklist completion
        handleSave();
      } else {
        setActivePage((prev) => prev + 1);
      }
    } else {
      setSnackbar({
        open: true,
        message: "Please complete all items in this page before continuing.",
        severity: "warning",
      });
    }
  };

  const handleItemUpdate = (itemId, workOrderLineItemId, updates) => {
    if (!onUpdateItem) return;

    // Find which page contains this item
    const itemLocation = findItemInPages(pages, itemId, workOrderLineItemId);
    if (!itemLocation) return;

    // Update the item through the parent callback
    onUpdateItem(checklist.id, workOrderLineItemId, itemId, updates);

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Show success message for completed items
    if (
      updates.completed &&
      !pages[itemLocation.pageIndex].items.find((item) => item.id === itemId)
        ?.completed
    ) {
      console.log(pages);
      setSnackbar({
        open: true,
        message: "Item completed!",
        severity: "success",
      });
    }
  };

  const handleSave = async () => {
    if (!onSave) return;

    try {
      setIsSaving(true);
      await onSave(checklist);
      setHasUnsavedChanges(false);

      // Check if checklist is 100% complete
      console.log("progress.overallProgress:", progress.overallProgress);
      console.log(
        "progress.overallProgress === 100:",
        progress.overallProgress === 100
      );
      if (progress.overallProgress === 100) {
        console.log(
          "onSetPdfDialogOpen about to be called:",
          progress.overallProgress === 100
        );
        // Show PDF confirmation dialog instead of regular success message
        onSetPdfDialogOpen(true);
        console.log(
          "setPdfDialogOpen(true) called, pdfDialogOpen should be true"
        );
      } else {
        // Show regular success message for incomplete checklists
        setSnackbar({
          open: true,
          message: "Checklist saved successfully!",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error saving checklist:", error);
      setSnackbar({
        open: true,
        message: "Failed to save checklist. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // const handlePdfConfirm = async () => {
  //   try {
  //     // Extract parameters needed for PDF generation
  //     const pdfParams = {
  //       caseId: fsmParams.caseId || "",
  //       serviceAppointmentNumber: fsmParams.serviceAppointmentNumber || "",
  //     };

  //     console.log("Generating PDF with params:", pdfParams);

  //     // Call the PDF service
  //     await checklistService.savePdf(pdfParams);

  //     // Show success message
  //     setSnackbar({
  //       open: true,
  //       message: "PDF saved successfully!",
  //       severity: "success",
  //     });
  //   } catch (error) {
  //     console.error("Error saving PDF:", error);
  //     setSnackbar({
  //       open: true,
  //       message: "Failed to save PDF. Please try again.",
  //       severity: "error",
  //     });
  //   }
  // };

  // const handlePdfCancel = () => {
  //   // Show the regular success message when PDF is cancelled
  //   setSnackbar({
  //     open: true,
  //     message: "Checklist saved successfully!",
  //     severity: "success",
  //   });
  // };

  // const handleClosePdfDialog = () => {
  //   console.log("handleClosePdfDialog called - closing dialog");
  //   setPdfDialogOpen(false);
  // };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {/* Checklist Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          gutterBottom
          sx={{
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            fontWeight: 600,
          }}
        >
          Template Used: {checklist.templateName}
        </Typography>
        {/* TODO reinsert when progress calculation improves */}
        {/* <Typography variant="body2" color="text.secondary">
          <strong>{progress.completedPages}</strong> of{" "}
          <strong>{progress.totalPages}</strong> assets complete
        </Typography> */}
      </Paper>

      {/* Page Navigation */}
      <PageNavigation
        pages={pages}
        activePage={activePage}
        onPageClick={handlePageClick}
        allowNavigation={allowSkipping || !requirePageCompletion}
        progress={progress}
      />

      {/* Page Content */}
      <Box sx={{ mb: 3 }}>
        <PageContent
          page={currentPage}
          onUpdateItem={handleItemUpdate}
          editable={true}
          showProgress={true}
        />
      </Box>

      {/* Page Actions */}
      <PageActions
        activePage={activePage}
        totalPages={pages.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSave={handleSave}
        canGoNext={canGoNext}
        canGoPrevious={canGoPrevious}
        isLastPage={isLastPage}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        currentPageComplete={currentPage?.completed || false}
      />

      {/* Unsaved Changes Warning */}
      {hasUnsavedChanges && (
        <Alert
          severity="warning"
          sx={{
            mt: 2,
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
            },
          }}
        >
          You have unsaved changes. Don't forget to save your progress!
        </Alert>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* PDF Confirmation Dialog */}
      {/* <ConfirmationDialog
        open={pdfDialogOpen}
        onClose={handleClosePdfDialog}
        onConfirm={handlePdfConfirm}
        title="Save as PDF"
        message="You've completed this checklist. Would you like to save the PDF into Salesforce"
        confirmText="Save PDF"
        cancelText="Skip"
        confirmColor="primary"
      /> */}
    </Box>
  );
};

export default ChecklistStepper;
